import Moment from 'moment';
import {getFilterFunctions} from '../../../shared/components/filters/filter_logic';
import {PERMISSION_TYPE} from '../../../../Permissions';
import {hasSomePermission} from '../../../shared/util/PermissionsUtil';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {HIDDEN_FEATURES, MAX_BULK_SELECTION, ESTIMATION_UNIT, BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../constants';
import {GROUP_TYPE} from '../../../shared/components/task-tables/task-table-v3/TaskTable';
import {groupBy} from 'lodash';
import ProjectUtil from '../../../shared/util/ProjectUtil';
import UpdateTaskMutation from '../../../../mutations/update_task_mutation.modern';
import {ValueSource} from '../project-settings/financials/ValueCalculationsComponent';
import {isBillableSplitAllowed} from '../../../shared/util/cache/TimeRegistrationSettingsUtil';
import CompanySetupUtil, {isFeatureHidden} from '../../../shared/util/CompanySetupUtil';
import {shouldShowTaskOverrun} from '../../../shared/util/PredictionUtil';
import {
	getRemainingAvailabilityTooltip,
	getTotalAvailabilityTooltip,
} from '../../../project-tab/projects/scoping-page/ResourceUtil';
import {remapOptionTranslationIds} from '../../../shared/util/FinancialInternationalisationUtil';

const SCOPING_GROUP_KEY = 'project_scoping_group_setting';

export const CUSTOM_FIELD_PREFIX = 'customField_';

export const getCustomFieldColumnName = (entityType, key) => CUSTOM_FIELD_PREFIX + entityType + '_' + key;

export const isEmpty = obj => {
	for (var key in obj) {
		if (obj.hasOwnProperty(key)) return false;
	}
	return true;
};

export const tabs = [
	{translationId: 'common.all_phases', value: 'all'},
	{translationId: 'common.active_phases', value: 'active'},
	{translationId: 'common.past_phases', value: 'done'},
];

export const getInitialSelectedTab = () => {
	const storedTab = localStorage.getItem('project-scoping-selected-tab');
	if (storedTab) {
		return tabs.find(tab => tab.value === storedTab);
	} else return tabs[0];
};

function getScopingColumns(
	formatMessage,
	canViewFinancial,
	sprintsEnabled,
	viewer,
	isEstimatedInNewPoints,
	periodEnabled,
	showRevenue,
	showTime,
	showBillableTimeSplit
) {
	const {company, project} = viewer;
	const {customFieldDefinitionsV2, isUsingProjectAllocation, isUsingMixedAllocation} = company;
	const isDemoProject = project?.demo;
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');
	const hasUseFinancialServiceInScopingFF = hasFeatureFlag('use_financial_service_in_scoping');
	const isProjectEstimationUnitInHours = viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS;
	const isTimeAndMaterialsProject = project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS;
	const isFixedHoursRetainer =
		project.budgetType === BUDGET_TYPE.RETAINER && project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS;
	const isTimeAndMaterialsRetainer =
		project.budgetType === BUDGET_TYPE.RETAINER &&
		project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS;
	const isFixedPriceProject =
		project.budgetType === BUDGET_TYPE.FIXED_PRICE || project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const isFixedPriceRetainer =
		project.budgetType === BUDGET_TYPE.RETAINER && project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const shouldShowBillableValueTooltips =
		hasFinancialCategoriesUpdate && (isTimeAndMaterialsProject || isFixedHoursRetainer || isTimeAndMaterialsRetainer);
	const shouldShowBillableValueTooltipsFixedPrice =
		hasFinancialCategoriesUpdate && (isFixedPriceProject || isFixedPriceRetainer);

	const shouldShowOverrunPrediction = shouldShowTaskOverrun(
		!isEstimatedInNewPoints,
		PERMISSION_TYPE.PHASE_UPDATE,
		isDemoProject
	);
	const availableColumns = [];
	availableColumns.push({
		checked: true,
		width: 30,
		name: 'selector',
		translationId: null,
		hide: true,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 24,
		name: 'expand',
		translationId: null,
		hide: true,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 65,
		name: 'task-id',
		translationId: 'project_board.card_details_selector_task_id',
		hide: false,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 200,
		name: 'task-name',
		translationId: 'common.task_project',
		hide: true,
		align: 'left',
	});
	if (isUsingProjectAllocation || isUsingMixedAllocation) {
		availableColumns.push({
			checked: true,
			width: 100,
			name: 'total_availability',
			translationId: 'common.total_availability',
			hide: false,
			align: 'right',
			getTooltipItems: getTotalAvailabilityTooltip,
		});
		availableColumns.push({
			checked: true,
			width: 100,
			name: 'remaining_availability',
			translationId: 'common.remaining_availability',
			hide: false,
			align: 'right',
			getTooltipItems: getRemainingAvailabilityTooltip,
		});
	}
	availableColumns.push({
		checked: true,
		width: 211,
		name: 'date',
		translationId: 'common.dates',
		hide: false,
		align: 'center',
	});
	if (customFieldDefinitionsV2) {
		customFieldDefinitionsV2.edges
			.map(edge => edge.node)
			.filter(definition => ['TASK', 'PHASE'].includes(definition.entityType))
			.forEach(definition => {
				availableColumns.push({
					checked: true,
					width: 166,
					name: getCustomFieldColumnName(definition.entityType, definition.key),
					displayName:
						definition.displayName +
						' (' +
						formatMessage({id: 'settings.custom-fields.entity-type.' + definition.entityType}) +
						')',
					customFieldKey: definition.key,
					entityType: definition.entityType,
					readOnly: !!definition.readOnly,
					hide: false,
					align: 'left',
				});
			});
	}
	availableColumns.push({
		checked: true,
		width: 166,
		name: 'assigned-role',
		translationId: 'common.role',
		hide: false,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 166,
		name: 'assigned-person',
		translationId: 'common.assignees',
		hide: false,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 150,
		name: 'status',
		translationId: 'common.status',
		hide: false,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 150,
		name: 'phase',
		translationId: 'common.phase',
		hide: false,
		align: 'left',
	});

	sprintsEnabled &&
		availableColumns.push({
			checked: true,
			width: 150,
			name: 'sprint',
			translationId: 'common.sprint',
			hide: false,
			align: 'left',
		});
	const manualProgressOnTasksEnabled = project.manualProgressOnTasksEnabled;
	const manualProgressOnPhasesEnabled = project.manualProgressOnPhasesEnabled;
	const isManualProgressDisabled = !manualProgressOnTasksEnabled && !manualProgressOnPhasesEnabled;
	availableColumns.push({
		checked: true,
		width: 70,
		name: 'done-percentage',
		translationId: 'common.progress.short',
		hide: false,
		align: 'right',
		getTooltipItems: formatMessage => {
			if (hasFinancialCategoriesUpdate && isManualProgressDisabled) {
				return [
					{
						title: formatMessage({id: 'common.progress.short'}),
						description: formatMessage({id: 'project_scoping.progress_tooltip_new'}),
						details: [
							formatMessage({id: 'common.actual_hours'}),
							'/',
							formatMessage({id: 'common.projected_total_hours'}),
							'x',
							'100',
						],
					},
				];
			}
			return [
				{
					title: formatMessage({id: 'common.progress.short'}),
					details: [
						formatMessage({id: 'common.estimate'}),
						'-',
						formatMessage({id: 'common.remaining'}),
						'/',
						formatMessage({id: 'common.estimate'}),
						'x',
						'100',
					],
				},
			];
		},
	});

	// Show period column if period is enabled
	periodEnabled &&
		availableColumns.push({
			checked: true,
			width: 110,
			name: 'period-target',
			translationId: 'common.period-target',
			hide: true,
			align: 'right',
		});

	const estimatedTimeTranslationId = isProjectEstimationUnitInHours ? 'common.estimated_hours' : 'common.estimated_points';
	availableColumns.push({
		checked: true,
		width: shouldShowOverrunPrediction ? 112 : 90,
		name: 'forecast',
		translationId: hasFinancialCategoriesUpdate ? estimatedTimeTranslationId : 'common.estimate',
		hide: false,
		align: 'right',
		getTooltipItems: formatMessage => {
			if (hasFinancialCategoriesUpdate && isProjectEstimationUnitInHours) {
				return [
					{
						title: formatMessage({id: 'common.estimated_hours'}),
						description: formatMessage({id: 'project_scoping.estimated_hours_tooltip'}),
					},
				];
			}
			if (hasFinancialCategoriesUpdate && !isProjectEstimationUnitInHours) {
				return [
					{
						title: formatMessage({id: 'common.estimated_points'}),
						description: formatMessage({id: 'project_scoping.estimated_points_tooltip'}),
					},
				];
			}
			return [{description: formatMessage({id: 'project_section.tooltip_task_estimates'})}];
		},
	});
	availableColumns.push({
		checked: true,
		width: 90,
		name: 'time-entries',
		translationId: hasFinancialCategoriesUpdate ? 'common.actual_hours' : 'common.time_entries',
		hide: false,
		align: 'right',
		getTooltipItems: formatMessage => {
			if (hasFinancialCategoriesUpdate) {
				return [
					{
						title: formatMessage({id: 'common.actual_hours'}),
						description: formatMessage({id: 'project_scoping.actual_hours_tooltip'}),
					},
				];
			}
			return [{description: formatMessage({id: 'project_section.tooltip_task_time_entries'})}];
		},
	});
	if (showBillableTimeSplit && showRevenue) {
		availableColumns.push({
			checked: true,
			width: 90,
			name: 'billable-time-entries',
			translationId: hasFinancialCategoriesUpdate ? 'common.actual_billable_hours' : 'utilization.billable_time',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (hasFinancialCategoriesUpdate) {
					return [
						{
							title: formatMessage({id: 'common.actual_billable_hours'}),
							description: formatMessage({id: 'project_scoping.actual_billable_hours_tooltip'}),
						},
					];
				}
				return [];
			},
		});
		availableColumns.push({
			checked: true,
			width: 90,
			name: 'non-billable-time-entries',
			translationId: hasFinancialCategoriesUpdate ? 'common.actual_non_billable_hours' : 'utilization.non_billable_time',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (hasFinancialCategoriesUpdate) {
					return [
						{
							title: formatMessage({id: 'common.actual_non_billable_hours'}),
							description: formatMessage({id: 'project_scoping.actual_non_billable_hours_tooltip'}),
						},
					];
				}
				return [];
			},
		});
	}

	const remainingWorkTranslationId = isProjectEstimationUnitInHours
		? 'common.remaining_work_hours'
		: 'common.remaining_work_points';
	showTime &&
		availableColumns.push({
			checked: true,
			width: 90,
			name: 'remaining',
			translationId: hasFinancialCategoriesUpdate ? remainingWorkTranslationId : 'common.remaining',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (hasFinancialCategoriesUpdate && isProjectEstimationUnitInHours) {
					return [
						{
							title: formatMessage({id: 'common.remaining_work_hours'}),
							description: formatMessage({id: 'project_scoping.remaining_work_hours_tooltip'}),
						},
					];
				}
				if (hasFinancialCategoriesUpdate && !isProjectEstimationUnitInHours) {
					return [
						{
							title: formatMessage({id: 'common.remaining_work_points'}),
							description: formatMessage({id: 'project_scoping.remaining_work_points_tooltip'}),
						},
					];
				}
				return [{description: formatMessage({id: 'project_section.tooltip_task_time_remaining'})}];
			},
		});

	const estimatedVsActualTranslationId = isProjectEstimationUnitInHours
		? 'common.estimated_vs_actual_hours'
		: 'common.estimated_vs_actual_points';
	availableColumns.push({
		checked: true,
		width: 90,
		name: 'over-forecast',
		translationId: hasFinancialCategoriesUpdate ? estimatedVsActualTranslationId : 'project_scoping.difference_forecast',
		hide: false,
		align: 'right',
		getTooltipItems: formatMessage => {
			if (hasFinancialCategoriesUpdate && isProjectEstimationUnitInHours) {
				return [
					{
						title: formatMessage({id: 'common.estimated_vs_actual_hours'}),
						description: formatMessage({id: 'project_scoping.estimated_vs_actual_hours_tooltip'}),
						details: [
							formatMessage({id: 'common.estimated_hours'}),
							'-',
							formatMessage({id: 'common.actual_hours'}),
						],
					},
				];
			}
			if (hasFinancialCategoriesUpdate && !isProjectEstimationUnitInHours) {
				return [
					{
						title: formatMessage({id: 'project_scoping.estimated_points_vs_actual_hours'}),
						description: formatMessage({id: 'project_scoping.estimated_points_vs_actual_hours_tooltip'}),
						details: [
							'(',
							formatMessage({id: 'common.estimated_points'}),
							'x',
							formatMessage({id: 'settings.estimation-hours-pr-point'}),
							')',
							'-',
							formatMessage({id: 'common.actual_hours'}),
						],
					},
				];
			}
			return [
				{
					title: formatMessage({id: 'project_scoping.difference_forecast'}),
					details: [formatMessage({id: 'common.estimate'}), '-', formatMessage({id: 'common.time_entries'})],
				},
			];
		},
	});

	showRevenue &&
		canViewFinancial &&
		project.financialSourceSettings.plannedRevenue !== ValueSource.ALLOCATION &&
		!hasFinancialCategoriesUpdate &&
		availableColumns.push({
			checked: true,
			width: 110,
			name: 'price',
			translationId: 'project_budget.plan_revenue',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => [
				{
					title: formatMessage({id: 'project_budget.planned_billable_time'}),
					details: [formatMessage({id: 'common.estimate'}), 'x', formatMessage({id: 'common.rate'})],
				},
			],
		});

	canViewFinancial &&
		showRevenue &&
		project.financialSourceSettings.actualRevenue !== ValueSource.ALLOCATION &&
		availableColumns.push({
			checked: true,
			width: 110,
			name: 'actual-price',
			translationId: hasFinancialCategoriesUpdate
				? 'common.actual_billable_value_of_service'
				: 'project_budget.actual_billable_time',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (shouldShowBillableValueTooltipsFixedPrice) {
					return [
						{
							title: formatMessage({id: 'common.actual_billable_value_of_service'}),
							description: formatMessage({
								id: 'project_scoping.actual_billable_value_of_service_fixed_price_tooltip',
							}),
							details: [
								formatMessage({id: 'common.actual_billable_hours'}),
								'x',
								formatMessage({id: 'common.rate'}),
							],
						},
					];
				}
				if (shouldShowBillableValueTooltips) {
					return [
						{
							title: formatMessage({id: 'common.actual_billable_value_of_service'}),
							description: formatMessage({id: 'project_scoping.actual_billable_value_of_service_tooltip'}),
							details: [
								formatMessage({id: 'common.actual_billable_hours'}),
								'x',
								formatMessage({id: 'common.rate'}),
							],
						},
					];
				}
				return [
					{
						title: formatMessage({id: 'project_budget.actual_billable_time'}),
						details: [formatMessage({id: 'common.time_entries'}), 'x', formatMessage({id: 'common.rate'})],
					},
				];
			},
		});

	showRevenue &&
		canViewFinancial &&
		project.financialSourceSettings.forecastRevenue !== ValueSource.ALLOCATION &&
		hasFinancialCategoriesUpdate &&
		availableColumns.push({
			checked: true,
			width: 110,
			name: 'projected-billable-value-of-service',
			translationId: 'common.projected_total_billable_value_of_service',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (shouldShowBillableValueTooltipsFixedPrice) {
					return [
						{
							title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
							description: formatMessage({
								id: 'project_scoping.projected_total_billable_value_of_service_fixed_price_tooltip',
							}),
							details: [
								'(',
								formatMessage({id: 'common.actual_billable_hours'}),
								'+',
								formatMessage({id: 'common.remaining_work_billable_hours'}),
								')',
								'x',
								formatMessage({id: 'common.rate'}),
							],
						},
					];
				}
				if (shouldShowBillableValueTooltips) {
					return [
						{
							title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
							description: formatMessage({
								id: 'project_scoping.projected_total_billable_value_of_service_tooltip',
							}),
							details: [
								'(',
								formatMessage({id: 'common.actual_billable_hours'}),
								'+',
								formatMessage({id: 'common.remaining_work_billable_hours'}),
								')',
								'x',
								formatMessage({id: 'common.rate'}),
							],
						},
					];
				}
				return [];
			},
		});

	canViewFinancial &&
		project.financialSourceSettings.plannedCost !== ValueSource.ALLOCATION &&
		hasUseFinancialServiceInScopingFF &&
		!hasFinancialCategoriesUpdate &&
		availableColumns.push({
			checked: false,
			width: 110,
			name: 'planned-cost',
			translationId: 'project_budget.planned_cost',
			hide: false,
			align: 'right',
		});

	canViewFinancial &&
		project.financialSourceSettings.actualCost !== ValueSource.ALLOCATION &&
		hasUseFinancialServiceInScopingFF &&
		availableColumns.push({
			checked: false,
			width: 110,
			name: 'actual-cost',
			translationId: hasFinancialCategoriesUpdate ? 'common.actual_cost' : 'project_budget.actual_cost_to_date',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (hasFinancialCategoriesUpdate) {
					return [
						{
							title: formatMessage({id: 'common.actual_cost'}),
							description: formatMessage({id: 'project_scoping.actual_cost_tooltip'}),
							details: [
								formatMessage({id: 'common.actual_hours'}),
								'x',
								formatMessage({id: 'common.internal_hourly_cost'}),
							],
						},
					];
				}
				return [];
			},
		});

	canViewFinancial &&
		project.financialSourceSettings.plannedCost !== ValueSource.ALLOCATION &&
		hasUseFinancialServiceInScopingFF &&
		hasFinancialCategoriesUpdate &&
		availableColumns.push({
			checked: false,
			width: 110,
			name: 'projected-cost',
			translationId: 'common.projected_total_cost',
			hide: false,
			align: 'right',
			getTooltipItems: formatMessage => {
				if (hasFinancialCategoriesUpdate) {
					return [
						{
							title: formatMessage({id: 'common.projected_total_cost'}),
							description: formatMessage({id: 'project_scoping.projected_total_cost_tooltip'}),
							details: [
								'(',
								formatMessage({id: 'common.actual_hours'}),
								'+',
								formatMessage({id: 'common.remaining_work_hours'}),
								')',
								'x',
								formatMessage({id: 'common.internal_hourly_cost'}),
							],
						},
					];
				}
				return [];
			},
		});

	availableColumns.push({
		checked: true,
		width: 300,
		name: 'labels',
		translationId: 'common.labels',
		hide: false,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 90,
		name: 'approved',
		translationId: 'common.approved',
		hide: false,
		align: 'center',
	});
	availableColumns.push({
		checked: true,
		width: 60,
		name: 'starred',
		translationId: null,
		hide: true,
		align: 'left',
	});
	availableColumns.push({
		checked: true,
		width: 20,
		name: 'context-menu',
		translationId: null,
		hide: true,
		align: 'left',
	});

	remapOptionTranslationIds(availableColumns);
	return availableColumns;
}

export const getInitialSelectedColumns = (formatMessage, viewer, showPeriods) => {
	const storedCols = JSON.parse(localStorage.getItem('project-new-scoping-selected-cols'));
	const hasFinancialAccess =
		CompanySetupUtil.hasFinance() &&
		hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE]);
	const sprintsEnabled = viewer.project && viewer.project.sprintTimeBox;
	const isEstimatedInNewPoints = viewer.project.estimationUnit === 'POINTS';
	const showRevenue = ProjectUtil.projectTracksRevenue(viewer.project);
	const showTime = ProjectUtil.projectTracksTime(viewer.project) || viewer.project.manualProgressOnTasksEnabled;
	const showBillableTimeSplit = isBillableSplitAllowed();
	const availableCols = getScopingColumns(
		formatMessage,
		hasFinancialAccess,
		sprintsEnabled,
		viewer,
		isEstimatedInNewPoints,
		showPeriods,
		showRevenue,
		showTime,
		showBillableTimeSplit
	);
	// Replace localStorage if the stored columns differ in length, or if their IDs are different
	const shouldReplace = (stored, avail) =>
		stored.length !== avail.length || stored.filter((c, i) => c.id !== avail[i].name).length > 0;

	if (storedCols) {
		for (let i = 0; i < availableCols.length; i++) {
			const col = availableCols[i];
			const storedCol = storedCols.find(storedCol => storedCol.id === col.name);
			if (!col.hide) {
				availableCols[i].checked = storedCol ? !!storedCol.checked : col.checked;
			}
		}
		if (shouldReplace(storedCols, availableCols)) {
			Util.localStorageSetItem(
				'project-scoping-selected-cols',
				JSON.stringify(
					availableCols.map(c => {
						return {
							id: c.name,
							checked: c.checked,
						};
					})
				)
			);
		}
	}

	//If no time registration is enabled, remove related columns
	if (ProjectUtil.projectUsesManualProgress(viewer.project) || isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
		return Util.getNoTimeRegColumns(
			availableCols,
			viewer.project.manualProgressOnProjectEnabled ? 'done-percentage' : undefined
		);
	}

	return availableCols;
};

export const getOrderedPhaseIds = phases => {
	return Util.sortPhases(phases, false).map(phase => phase.id);
};

export const getExpandedPhasesMap = phases => {
	const map = new Map();
	phases.forEach(phase => {
		map.set(phase.id, true);
	});

	return map;
};

export const getExpandedTaskMap = tasks => {
	const map = new Map();
	tasks.forEach(task => {
		map.set(task.id, false);
	});

	return map;
};

export const getPhasesFilters = (projectId, preAppliedFilters) => {
	let filtersKey = 'project-scoping-filters-v4-';

	if (preAppliedFilters) {
		return getFilterFunctions(preAppliedFilters);
	} else if (localStorage.getItem(`${filtersKey}${projectId}`)) {
		const filters = JSON.parse(localStorage.getItem(`${filtersKey}${projectId}`));
		return getFilterFunctions(filters);
	} else {
		const filters = {
			person: {},
			task: {},
		};
		return getFilterFunctions(filters);
	}
};

export const compareSprintDates = (a, b) => {
	let aStartDate = Moment({
		y: a.node.startYear,
		M: a.node.startMonth - 1,
		d: a.node.startDay,
	});
	let aEndDate = Moment({
		M: a.node.endMonth - 1,
		y: a.node.endYear,
		d: a.node.endDay,
	});
	let bStartDate = Moment({
		y: b.node.startYear,
		M: b.node.startMonth - 1,
		d: b.node.startDay,
	});
	let bEndDate = Moment({
		y: b.node.endYear,
		M: b.node.endMonth - 1,
		d: b.node.endDay,
	});
	//Date used for sorting sprints with not selected dates
	const dummyDate = Moment({
		y: -10000,
		M: 1,
		d: 1,
	});

	if (!aStartDate.isValid()) aStartDate = dummyDate.clone();
	if (!aEndDate.isValid()) aEndDate = dummyDate.clone();
	if (!bStartDate.isValid()) bStartDate = dummyDate.clone();
	if (!bEndDate.isValid()) bEndDate = dummyDate.clone();

	if (aStartDate.isBefore(bStartDate)) return 1;
	if (bStartDate.isBefore(aStartDate)) return -1;
	//If same date, sort by end date ascending
	if (aEndDate.isBefore(bEndDate)) return 1;
	if (bEndDate.isBefore(aEndDate)) return -1;

	const aId = parseInt(atob(a.node.id).replace('Sprint:', ''), 10);
	const bId = parseInt(atob(b.node.id).replace('Sprint:', ''), 10);
	if (aId < bId) return 1;
	return -1;
};

export const getInitialGroupType = () => {
	const newGroupSetting = localStorage.getItem(SCOPING_GROUP_KEY);
	if (newGroupSetting && Object.values(GROUP_TYPE).some(type => type === newGroupSetting)) {
		return newGroupSetting;
	}

	if (JSON.parse(localStorage.getItem('project-scoping-group-by-person'))) {
		return GROUP_TYPE.PERSON;
	}
	if (JSON.parse(localStorage.getItem('project-scoping-group-by-role'))) {
		return GROUP_TYPE.ROLE;
	}

	return GROUP_TYPE.NO_GROUPING;
};

/**
 * Find all children and grandchildren of the selected task/tasks
 * @param tasksToAdd Lists of tasks to be selected
 * @param allTasks All tasks
 * @returns List of all the children and grandchildren of the tasksToAdd
 */
const _getSubtasksFromSelectedTasks = (tasksToAdd, allTasks) => {
	const subtaskMap = groupBy(allTasks, 'node.parentTaskId');
	let subtasksToSelect = [];
	let tasksToSelect = [];
	// Find children tasks
	tasksToAdd.forEach(t => {
		let tasks = subtaskMap[t.id];
		if (tasks) {
			tasks = tasks.map(task => {
				return task.node;
			});
			subtasksToSelect = subtasksToSelect.concat(tasks);
			tasksToSelect = tasksToSelect.concat(tasks);
		}
	});
	// Find grandchildren tasks
	subtasksToSelect.forEach(t => {
		let tasks = subtaskMap[t.id];
		if (tasks) {
			tasks = tasks.map(task => {
				return task.node;
			});
			tasksToSelect = tasksToSelect.concat(tasks);
		}
	});
	// Array of children and grandchildren to be selected
	return tasksToSelect;
};

export const updateSelectTasks = (
	viewer,
	intl,
	selectedTasks,
	taskToSelect,
	shiftKey,
	createToast,
	setSelectedTasks,
	groupSetting,
	treeData
) => {
	const isGrouped = groupSetting !== GROUP_TYPE.NO_GROUPING;
	// Deep copy to avoid messing with previously selected tasks
	let selected = [...selectedTasks];
	const alreadySelected = selected.find(elem => elem.id === taskToSelect.id);
	// Always prioritize unselect if already selected
	if (alreadySelected) {
		// Remove selected element
		selected.splice(
			selected.findIndex(elem => elem.id === taskToSelect.id),
			1
		);
	} else {
		// Do multi select on shift, if a previous task (starting point) was selected
		if (shiftKey && selectedTasks.length > 0) {
			const flatten = array =>
				array.flatMap(({task, children}) => [task, ...flatten(children || [])]).filter(node => node);
			const flattenTask = flatten(treeData);

			let tasksToAdd = [];
			const start = selected[selected.length - 1];
			const end = taskToSelect;
			if (start && end) {
				const startIndex = flattenTask.findIndex(task => task.id === start.id) || 0;
				const endIndex = flattenTask.findIndex(task => task.id === end.id);
				if (startIndex < endIndex) {
					// +1 to start to avoid adding the already added task
					tasksToAdd = flattenTask.slice(startIndex, endIndex + 1);
				} else {
					// Slice always goes from 0..n, so flip direction of going from end to start
					tasksToAdd = flattenTask.slice(endIndex, startIndex + 1);
				}
				// Avoid adding elements that were previously added
				tasksToAdd = tasksToAdd.filter(task => !selected.find(t => t.id === task.id));
				selected = selected.concat(tasksToAdd);
				// If there is no grouping then select the children and grandchildren of the selected tasks
				if (!isGrouped)
					selected = selected.concat(_getSubtasksFromSelectedTasks(tasksToAdd, viewer.project.tasks.edges));
			}
		}
		// Else add as normally
		else {
			selected.push(taskToSelect);
			// If there is no grouping then select the children and grandchildren of the selected tasks
			if (!isGrouped)
				selected = selected.concat(_getSubtasksFromSelectedTasks([taskToSelect], viewer.project.tasks.edges));
		}
	}
	if (selected.length > MAX_BULK_SELECTION) {
		createToast({
			duration: 5000,
			message: intl.formatMessage({id: 'common.max_bulk_selected'}, {tasks: MAX_BULK_SELECTION}),
		});
	}
	// remove duplicates
	selected = selected.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
	setSelectedTasks(selected.length > MAX_BULK_SELECTION ? selected.slice(0, MAX_BULK_SELECTION) : selected);
};

export const updateTaskForScoping = (taskId, onSuccess) => {
	const isScoping = window.location.pathname.includes('/scoping');
	if (hasFeatureFlag('use_financial_service_in_scoping') && !hasFeatureFlag('financial_socket_push') && isScoping && taskId) {
		// updating the task to recalculate the task financials - only when registering time on scoping to prevent overhead anywhere else
		Util.CommitMutation(UpdateTaskMutation, {ids: [taskId]}, onSuccess);
	} else if (onSuccess) {
		onSuccess();
	}
};

export const getHiddenFinancialFields = (financialSourceSettings, intl) => {
	const hiddenFinancialFields = [];

	if (!financialSourceSettings) return hiddenFinancialFields;

	if (financialSourceSettings.plannedRevenue !== ValueSource.TASK) {
		hiddenFinancialFields.push(intl.formatMessage({id: 'project_budget.planned_billable_time'}));
	}
	if (financialSourceSettings.plannedCost !== ValueSource.TASK) {
		hiddenFinancialFields.push(intl.formatMessage({id: 'project_budget.planned_cost'}));
	}
	if (financialSourceSettings.actualRevenue !== ValueSource.TASK) {
		hiddenFinancialFields.push(intl.formatMessage({id: 'project_budget.actual_billable_time_short'}));
	}
	if (financialSourceSettings.actualCost !== ValueSource.TASK) {
		hiddenFinancialFields.push(intl.formatMessage({id: 'project_budget.actual_cost_to_date'}));
	}
	if (
		financialSourceSettings.actualRevenue !== ValueSource.TASK ||
		financialSourceSettings.forecastRevenue !== ValueSource.TASK
	) {
		hiddenFinancialFields.push(intl.formatMessage({id: 'project_budget.value_of_service_at_completion'}));
	}
	return hiddenFinancialFields;
};
